import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { VirtuosoMasonry } from "@virtuoso.dev/masonry";
import { MODELS, useMultiModel } from "../../api";
import PageShell from "../../design-system/components/PageShell/PageShell";
import Text from "../../design-system/primitives/components/Text/Text";
import BigTagButton from "../../design-system/primitives/components/BigTagButton/BigTagButton";
import HintBox, {
  HintKey,
} from "../../design-system/components/HintBox/HintBox.jsx";
import { LibraryItemPreview } from "./LibraryItemPreview.jsx";
import { useLibraryFilters } from "./useLibraryFilters";
import styles from "./library.css";

const typeToLabel = {
  Video: "video",
  ExternalLink: "link",
  RichText: "artikel",
};

const TagCloud = ({
  validTags,
  tagFilter,
  toggleTag,
  validTypes,
  typeFilter,
  toggleType,
  validCategories,
  categoryFilter,
  toggleCategory,
  spark,
  sparkFilter,
  toggleSpark,
}) => {
  const groups = [
    {
      key: "tag",
      values: [...validTags],
      filter: tagFilter,
      toggle: toggleTag,
      label: (tag) => tag.toLowerCase(),
    },
    {
      key: "type",
      values: [...validTypes],
      filter: typeFilter,
      toggle: toggleType,
      label: (type) => typeToLabel[type],
    },
    {
      key: "cat",
      values: [...validCategories],
      filter: categoryFilter,
      toggle: toggleCategory,
      label: (cat) => cat.toLowerCase(),
    },
    {
      key: "spark",
      values: [spark.id],
      filter: sparkFilter,
      toggle: toggleSpark,
      label: () => spark.name,
    },
  ];

  return (
    <div className={styles.tagCloud}>
      {groups.flatMap(({ key, values, filter, toggle, label }) =>
        values.map((value) => (
          <BigTagButton
            key={`${key}-${value}`}
            selected={filter.has(value)}
            onClick={() => toggle(value)}
          >
            #{label(value)}
          </BigTagButton>
        )),
      )}
    </div>
  );
};

/**
 * The virtualized masonry of library item previews. Fills the height it is
 * given and scrolls inside it — the ranking and the tag filters that produce
 * `data` live in `useLibraryFilters`, above the page shell.
 *
 * @param {Object} props
 * @param {Array} props.data - The items to lay out, in the order to lay them out.
 * @param {Object} props.context - Passed to every preview (tag highlighting).
 * @param {React.ReactNode} [props.overlay] - Pinned over the top of the masonry,
 *   out of the flow: items scroll underneath it and it costs the masonry no height.
 */
const ItemList = ({ data, context, overlay }) => {
  // The masonry picks its column count from a pixel width but hands us no
  // element to measure, so we measure the box we give it.
  //
  // Measured before paint, and the masonry is held back until we have a width:
  // mounting it at zero lays every item into a single column, and the later
  // column-count change doesn't re-flow the items it already placed.
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setWidth(container.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const columnCount = useMemo(
    () => Math.max(Math.ceil(width / 480), 1),
    [width],
  );

  return (
    <div ref={containerRef} className={styles.itemListContainer}>
      {overlay && <div className={styles.itemListOverlay}>{overlay}</div>}
      {width > 0 && (
        <VirtuosoMasonry
          columnCount={columnCount}
          data={data}
          context={context}
          ItemContent={LibraryItemPreview}
          // Both land on the scroller the masonry makes for itself: the class
          // gives it the sheet's inset, the style bounds its height (fill the
          // flex space) so it virtualizes instead of growing to fit every item.
          className={styles.masonryScroller}
          style={{ flex: 1, minHeight: 0 }}
        />
      )}
    </div>
  );
};

/**
 * Library page for a spark. The tag filters ride along in the shell's header so
 * they stay put, and the masonry takes over the shell's body (`bodySlot`) so it
 * spans the sheet and its scrollbar sits on the edge — with the external-content
 * hint floating over its top.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.items - The library items to display; an empty array
 *                              renders the "nothing here" message instead.
 * @param {Object} props.spark - The spark whose tags seed the item ranking.
 * @returns {JSX.Element} The rendered library overview page.
 */
const LibraryOverviewWithData = ({ items, spark }) => {
  const { data, tagCloudProps, itemContext } = useLibraryFilters(items, spark);

  const title = <Text variant="h4">Bibliothek</Text>;
  const titleButton = { icon: "arrow-left", ariaLabel: "Zurück", to: ".." };

  if (items.length === 0) {
    return (
      <PageShell surface="dark" titleSlot={title} titleButton={titleButton}>
        <div className={styles.noLibraryItemsText}>
          Keine Inhalte in der Bibliothek
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      surface="dark"
      titleSlot={
        <div className={styles.titleBlock}>
          {title}
          <TagCloud {...tagCloudProps} />
        </div>
      }
      titleButton={titleButton}
      bodySlot={
        <ItemList
          data={data}
          context={itemContext}
          overlay={
            <HintBox
              header="Hinweis: Externe Inhalte"
              hintKey={HintKey.libraryExternalContents}
            >
              Die folgenden Inhalte stammen teilweise von externen Anbietern.
              Wir haben sie sorgfältig für euch ausgewählt und überprüfen sie
              regelmäßig. Trotzdem können wir nicht für alle Inhalte eine
              Garantie übernehmen.
            </HintBox>
          }
        />
      }
    />
  );
};

/**
 * Represents the LibraryOverview component, responsible for displaying a library overview
 * page for a specific spark and workshop. It fetches related data, such as library items,
 * spark information,*/
const LibrayOverview = () => {
  const { sparkSlug } = useParams();
  const [items, spark] = useMultiModel([
    MODELS.LIBRARY_ITEMS,
    MODELS.SPARK({ slug: sparkSlug }),
  ]);

  return <LibraryOverviewWithData items={items} spark={spark} />;
};

export default LibrayOverview;
